import type { InventoryVehicle } from '@carvana/merch-contracts';
import { useEffectWithController } from '@carvana/merch-react-hooks';
import isEqual from 'lodash.isequal';
import { useContext } from 'react';
import useGetClientId from '~/constants/srp/getClientId';
import { useUserId, useUserLocation } from '~/context/common/UserContext';
import ErrorContext from '~/context/srp/ErrorContext';
import InventoryContext from '~/context/srp/InventoryContext';
import useMemoCompare from '~/hooks/srp/useMemoCompare';
import fetchVehiclesDeliveryPickup, {
  formatVehicleForRequest
} from '~/services/common/fetchVehiclesDeliveryPickup';
import { generateErrorLogger, logError } from '~/utilities/loggers';
import { convertAvailabilityVehiclesToMap } from './helpers';

export default function useGetAvailability() {
  const {
    inventoryBulkData: { setData: setBulkData },
    similarVehicles: { vehicles, searchRequestId }
  } = useContext(InventoryContext);
  const { updateResponseErrors } = useContext(ErrorContext);
  const { zip } = useUserLocation();
  const userId = useUserId();
  const clientId = useGetClientId();

  const stableVehicles = useMemoCompare<InventoryVehicle[]>(
    vehicles,
    (prev, next) =>
      isEqual(
        prev.map(vehicle => vehicle.vehicleId),
        next.map(vehicle => vehicle.vehicleId)
      )
  );

  const shouldCallAvailability = stableVehicles.length > 0;

  useEffectWithController(
    controller => {
      if (!shouldCallAvailability) return;

      fetchVehiclesDeliveryPickup(
        {
          clientId,
          correlationId: searchRequestId,
          userId,
          vehicles: stableVehicles.map(formatVehicleForRequest),
          zip
        },
        controller
      )
        .then(res => {
          if (res.data.status === 'Succeeded') {
            const vehicleAvailabilities = convertAvailabilityVehiclesToMap(
              res.data.content ?? []
            );

            const didAvailabilitySucceed = Object.values(
              vehicleAvailabilities
            ).some(d => d.soonestDelivery || d.soonestPickup);

            setBulkData(prev => ({
              ...prev,
              ...vehicleAvailabilities
            }));

            if (didAvailabilitySucceed) {
              updateResponseErrors({ lowAvailabilityError: null });
            }
          } else {
            logError(new Error(res.data.errorMessage), {
              location: 'Low Inventory Availability'
            });

            const vehicle = res.data.content?.find(
              v =>
                v.soonestDelivery?.errorMessage || v.soonestPickup?.errorMessage
            );

            const errorMessage =
              vehicle?.soonestDelivery?.errorMessage ||
              vehicle?.soonestPickup?.errorMessage;

            updateResponseErrors({ lowAvailabilityError: errorMessage });
          }
        })
        .catch(
          generateErrorLogger(
            'Availability',
            {},
            {
              callback: () => {
                updateResponseErrors({
                  lowAvailabilityError:
                    'Failed to fetch availability for low inventory.'
                });
              },
              controller
            }
          )
        );
    },
    [
      clientId,
      searchRequestId,
      setBulkData,
      shouldCallAvailability,
      stableVehicles,
      updateResponseErrors,
      userId,
      zip
    ]
  );
}
